import { useCallback, useMemo, useState } from "react";
const NO_WALLETS = [];
const noop = () => { };
/**
 * Creates a {@link Pairing} for a Konekt provider without wagmi.
 *
 * Starting the pairing calls `provider.connect({ signal })` and subscribes to `display_uri`.
 * Running the returned teardown aborts the connection and removes the listener. Provider chains
 * become the modal's default Explorer filter.
 *
 * Pass `sources` to list injected wallets alongside WalletConnect pairing. Connecting an injected
 * wallet goes to its source and never touches the provider.
 *
 * @param provider Provider to connect. It may be `undefined` while application setup is loading;
 * the returned pairing reports a readable error if started before the provider exists.
 */
export function useProviderPairing(provider, { sources } = {}) {
    /** `connected` is a getter on the provider, so a settled session has to nudge React to read it again. */
    const [, setSettled] = useState(0);
    const [error, setError] = useState();
    const start = useCallback((onUri) => {
        if (!provider) {
            setError("No provider to pair with. Pass one to useProviderPairing.");
            return noop;
        }
        if (provider.connected) {
            setSettled((n) => n + 1);
            return noop;
        }
        const controller = new AbortController();
        const stop = () => provider.off("display_uri", onUri);
        setError(undefined);
        provider.on("display_uri", onUri);
        void provider
            .connect({ signal: controller.signal })
            .then(() => setSettled((n) => n + 1))
            .catch((e) => {
            if (!controller.signal.aborted)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(stop);
        return () => {
            controller.abort();
            stop();
        };
    }, [provider]);
    const reset = useCallback(() => setError(undefined), []);
    const chains = useMemo(() => provider?.chains?.map((c) => c.id), [provider]);
    const local = sources?.length ? sources.flatMap((source) => source.wallets) : NO_WALLETS;
    const connectLocal = (wallet) => {
        sources?.find((source) => source.wallets.some((w) => w.id === wallet.id))?.connect(wallet);
    };
    return {
        connected: (provider?.connected ?? false) || (sources?.some((source) => source.connected) ?? false),
        local,
        connectLocal,
        start,
        reset,
        error,
        chains,
        projectId: provider?.projectId,
    };
}
//# sourceMappingURL=providerPairing.js.map